import React, { useState } from "react";
import { Music, Wallet, Settings, Download, DollarSign, Check, Undo } from "lucide-react";
import { formatDate } from "../../utils/dateFormat";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";

const AdminKJOpsTab = ({
  activeSubView,
  setActiveSubView,
  kjDetails,
  confirmedBookings,
  kjReportMonth,
  setKjReportMonth,
  kjReportYear,
  setKjReportYear,
  kjReport,
  loadingKjReport,
  onFetchKjAllowanceReport,
  onGenerateKjAllowancePDF,
}) => {
  const [payFilter, setPayFilter] = useState('all');
  const [kjNameFilter, setKjNameFilter] = useState('all');

  // Unique KJ names for filter dropdown
  const kjNames = [...new Set((kjReport?.entries || []).map(e => e.kj_name).filter(Boolean))].sort();

  const handleToggleKjPaid = async (entry) => {
    try {
      if (entry.kj_paid) {
        await axios.put(`${API}/admin/finance/kj-payment/${entry.booking_id}/mark-unpaid`);
        toast.success(`Reverted ${entry.kj_name} to pending`);
      } else {
        await axios.put(`${API}/admin/finance/kj-payment/${entry.booking_id}/mark-paid`);
        toast.success(`Marked ${entry.kj_name} as paid`);
      }
      onFetchKjAllowanceReport();
    } catch { toast.error("Failed to update payment status"); }
  };

  const handleMarkMonthPaid = async () => {
    const month = `${kjReportYear}-${String(kjReportMonth).padStart(2, '0')}`;
    if (!window.confirm(`Mark ALL external KJ sessions in ${month} as paid?`)) return;
    try {
      const res = await axios.put(`${API}/admin/finance/kj-payment/mark-month-paid?month=${month}`);
      toast.success(res.data.message);
      onFetchKjAllowanceReport();
    } catch { toast.error("Failed to mark month as paid"); }
  };

  const filteredEntries = kjReport?.entries?.filter(e => {
    if (payFilter === 'pending' && !(! e.kj_paid && e.payout > 0)) return false;
    if (payFilter === 'paid' && !e.kj_paid) return false;
    if (kjNameFilter !== 'all' && e.kj_name !== kjNameFilter) return false;
    return true;
  }) || [];
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          onClick={() => setActiveSubView(activeSubView === 'kjreport' ? null : 'kjreport')}
          className={`p-4 border-2 border-black font-bold text-left transition-all ${
            activeSubView === 'kjreport' ? 'bg-purple-500 text-white' : 'bg-white hover:bg-purple-50'
          }`}
        >
          <Wallet className="mb-2" size={24} />
          <div className="text-sm">KJ Allowance Report</div>
          <div className="text-xs mt-1">External KJ vs RN Member</div>
        </button>
        
        <button
          onClick={() => setActiveSubView(activeSubView === 'kjstatus' ? null : 'kjstatus')}
          className={`p-4 border-2 border-black font-bold text-left transition-all ${
            activeSubView === 'kjstatus' ? 'bg-purple-500 text-white' : 'bg-white hover:bg-purple-50'
          }`}>
          <Settings className="mb-2" size={24} />
          <div className="text-sm">Master KJ Status</div>
          <div className="text-xs mt-1">View KJ roster</div>
        </button>
      </div>

      {/* KJ Allowance Report */}
      {activeSubView === 'kjreport' && (
        <div className="brutal-card">
          <h3 className="text-xl font-bold mb-4">💰 KJ Allowance Report</h3>
          
          <div className="flex flex-wrap gap-3 items-end mb-4">
            <div>
              <label className="block text-xs font-bold mb-1">Month</label>
              <select className="brutal-input" value={kjReportMonth}
                onChange={(e) => setKjReportMonth(parseInt(e.target.value))}>
                {Array.from({length: 12}, (_, i) => (
                  <option key={i+1} value={i+1}>
                    {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][i]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold mb-1">Year</label>
              <select className="brutal-input" value={kjReportYear}
                onChange={(e) => setKjReportYear(parseInt(e.target.value))}>
                {[2024, 2025, 2026, 2027].map(y => (<option key={y} value={y}>{y}</option>))}
              </select>
            </div>
            <button onClick={onFetchKjAllowanceReport} className="brutal-btn-primary" disabled={loadingKjReport}>
              {loadingKjReport ? 'Loading...' : 'Generate Report'}
            </button>
          </div>

          {kjReport && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <div className="p-3 bg-purple-50 border-2 border-purple-300 text-center">
                  <p className="text-xl font-bold text-purple-700">{kjReport.total_sessions}</p>
                  <p className="text-xs text-gray-600">Total Sessions</p>
                </div>
                <div className="p-3 bg-green-50 border-2 border-green-300 text-center">
                  <p className="text-xl font-bold text-green-700">${kjReport.total_paid_out || 0}</p>
                  <p className="text-xs text-gray-600">Paid Out ({kjReport.paid_count || 0})</p>
                </div>
                <div className="p-3 bg-red-50 border-2 border-red-300 text-center">
                  <p className="text-xl font-bold text-red-700">${kjReport.total_pending_payout || 0}</p>
                  <p className="text-xs text-gray-600">Pending ({kjReport.pending_count || 0})</p>
                </div>
                <div className="p-3 bg-amber-50 border-2 border-amber-300 text-center">
                  <p className="text-xl font-bold text-amber-700">${kjReport.total_to_disburse}</p>
                  <p className="text-xs text-gray-600">Total to Disburse</p>
                </div>
              </div>

              {/* Payment Filter + Mark Month Paid */}
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {['all', 'pending', 'paid'].map(f => (
                  <button
                    key={f}
                    onClick={() => setPayFilter(f)}
                    className={`px-2.5 py-1 text-xs font-bold border-2 transition-colors ${
                      payFilter === f ? 'border-purple-500 bg-purple-600 text-white' : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                    data-testid={`kj-report-filter-${f}`}
                  >
                    {f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
                <select
                  value={kjNameFilter}
                  onChange={(e) => setKjNameFilter(e.target.value)}
                  className="px-2 py-1 text-xs border-2 border-gray-300 font-bold bg-white"
                  data-testid="kj-name-filter"
                >
                  <option value="all">All KJs</option>
                  {kjNames.map(name => <option key={name} value={name}>{name}</option>)}
                </select>
                {(kjReport.pending_count || 0) > 0 && (
                  <button
                    onClick={handleMarkMonthPaid}
                    className="px-3 py-1 bg-green-600 text-white text-xs font-bold border-2 border-black hover:bg-green-700 flex items-center gap-1"
                    data-testid="kj-mark-all-paid"
                  >
                    <Check size={12} /> Mark All Pending as Paid
                  </button>
                )}
                <button
                  onClick={() => onGenerateKjAllowancePDF(filteredEntries, payFilter === 'all' ? 'All' : payFilter.charAt(0).toUpperCase() + payFilter.slice(1), kjNameFilter === 'all' ? '' : kjNameFilter)}
                  className="px-3 py-1 bg-purple-700 text-white text-xs font-bold border-2 border-black hover:bg-purple-800 flex items-center gap-1"
                  data-testid="kj-export-pdf"
                >
                  <Download size={12} /> Export PDF
                </button>
                <span className="text-xs text-gray-500 ml-auto">{filteredEntries.length} session(s)</span>
              </div>

              {filteredEntries.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[700px] text-sm">
                    <thead>
                      <tr className="bg-gray-100 border-b-2 border-black">
                        <th className="text-left p-2 font-bold">KJ Name</th>
                        <th className="text-left p-2 font-bold">Session Date</th>
                        <th className="text-left p-2 font-bold">Time</th>
                        <th className="text-left p-2 font-bold">KJ Type</th>
                        <th className="text-right p-2 font-bold">Payout</th>
                        <th className="text-center p-2 font-bold">Payment</th>
                        <th className="text-center p-2 font-bold">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredEntries.map((entry, idx) => (
                        <tr key={idx} className={`border-b hover:bg-gray-50 ${!entry.kj_paid && entry.payout > 0 ? 'bg-red-50' : ''}`}>
                          <td className="p-2 font-bold">{entry.kj_name}</td>
                          <td className="p-2 font-mono">{formatDate(entry.date)}</td>
                          <td className="p-2 text-xs">{entry.slot_time}</td>
                          <td className="p-2">
                            <span className={`px-2 py-0.5 text-xs font-bold rounded ${
                              entry.status_tag === 'External KJ' || entry.status_tag === 'External KJ (Legacy)'
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-gray-100 text-gray-600'
                            }`}>{entry.status_tag}</span>
                          </td>
                          <td className={`p-2 text-right font-bold ${entry.payout > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                            ${entry.payout}
                          </td>
                          <td className="p-2 text-center">
                            {entry.payout === 0 ? (
                              <span className="px-1.5 py-0.5 bg-gray-200 text-gray-500 rounded text-[10px] font-bold">N/A</span>
                            ) : entry.kj_paid ? (
                              <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-bold">PAID</span>
                            ) : (
                              <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-bold">PENDING</span>
                            )}
                          </td>
                          <td className="p-2 text-center">
                            {entry.payout > 0 && (
                              <button
                                onClick={() => handleToggleKjPaid(entry)}
                                className={`px-2 py-0.5 text-[10px] font-bold border rounded flex items-center gap-1 mx-auto ${
                                  entry.kj_paid
                                    ? 'bg-amber-100 border-amber-400 text-amber-700 hover:bg-amber-200'
                                    : 'bg-green-100 border-green-400 text-green-700 hover:bg-green-200'
                                }`}
                                data-testid={`kj-pay-toggle-${entry.booking_id}`}
                              >
                                {entry.kj_paid ? <><Undo size={10} /> Undo</> : <><Check size={10} /> Pay</>}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-amber-50 border-t-2 border-black">
                        <td colSpan={4} className="p-2 font-bold text-right">Total Payout (filtered):</td>
                        <td className="p-2 text-right font-bold text-amber-700 text-lg">${filteredEntries.reduce((s, e) => s + e.payout, 0)}</td>
                        <td colSpan={2}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No completed KJ sessions for this period.</p>
              )}
            </>
          )}
        </div>
      )}

      {/* KJ Status Management */}
      {activeSubView === 'kjstatus' && (
        <div className="brutal-card">
          <h3 className="text-xl font-bold mb-4">🎤 KJ Roster</h3>
          <p className="text-sm text-gray-600 mb-4">
            KJ eligibility is managed in the <strong>Directory</strong> tab. Toggle the &quot;KJ Allowance&quot; column to mark KJs as External KJ ($35) or RN Member ($0).
          </p>
          <div className="space-y-2">
            {kjDetails.length === 0 ? (
              <p className="text-gray-500">No KJs registered yet.</p>
            ) : (
              kjDetails.map((kj, idx) => (
                <div key={idx} className="flex justify-between items-center p-3 bg-purple-50 border border-purple-200">
                  <div className="flex items-center gap-3">
                    <Music size={18} className="text-purple-500" />
                    <span className="font-bold">{kj.name}</span>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded font-bold ${
                    kj.eligible ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {kj.eligible ? '🎤 EXTERNAL KJ ($35)' : '🏠 RN MEMBER ($0)'}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* KJ Quick Stats */}
      <div className="brutal-card bg-gradient-to-r from-purple-50 to-pink-50">
        <h3 className="text-lg font-bold mb-4">🎤 KJ Statistics</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-white border-2 border-black">
            <p className="text-2xl font-bold text-purple-600">{kjDetails.length}</p>
            <p className="text-xs text-gray-500">Total KJs</p>
          </div>
          <div className="text-center p-4 bg-white border-2 border-black">
            <p className="text-2xl font-bold text-green-600">{kjDetails.filter(k => k.eligible).length}</p>
            <p className="text-xs text-gray-500">External KJs</p>
          </div>
          <div className="text-center p-4 bg-white border-2 border-black">
            <p className="text-2xl font-bold text-gray-600">{kjDetails.filter(k => !k.eligible).length}</p>
            <p className="text-xs text-gray-500">RN Members</p>
          </div>
          <div className="text-center p-4 bg-white border-2 border-black">
            <p className="text-2xl font-bold text-pink-600">
              {confirmedBookings.filter(b => b.kj_required && b.status === 'completed').length}
            </p>
            <p className="text-xs text-gray-500">Sessions Done</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminKJOpsTab;
